import React, { useEffect, useState } from 'react';
import { Plus, ClipboardList, CheckCircle2, Scissors } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, StatCard, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, statusColor, SIZES, groupLogsByDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { LogLines } from './Tailors';


export default function MyWork() {
  const { user } = useAuth();
  const [tailor, setTailor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [products, setProducts] = useState([]);
  const [fin, setFin] = useState(null);
  const [open, setOpen] = useState(false);
  const [complete, setComplete] = useState(null);

  const tid = user.tailorId;

  const load = () => {
    api.get('/tailors').then((r) => setTailor(r.data.find((t) => t.id === tid)));
    api.get('/daily-logs').then((r) => setLogs(r.data.filter((l) => l.tailorId === tid)));
    api.get('/jobs').then((r) => setJobs(r.data));
    api.get('/products').then((r) => setProducts(r.data));
    if (tid) api.get(`/tailors/${tid}/financials`).then((r) => setFin(r.data));
  };
  useEffect(() => { load(); }, []);

  if (!tid) {
    return (
      <div>
        <PageHeader title="My Timesheet" />
        <EmptyState title="No tailor profile linked" description="Ask an administrator to link your account to a tailor record." icon={ClipboardList} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="My Timesheet"
        subtitle={`${tailor?.name || ''} · Rate ${fmtMoney(tailor?.paymentRate || 0)}/dozen`}
        actions={<Button onClick={() => setOpen(true)} data-testid="submit-log-btn"><Plus size={16} /> Log Output</Button>} />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Earned" value={fmtMoney(fin?.totalEarned)} />
        <StatCard label="Paid Out" value={fmtMoney(fin?.totalPaid)} accent="text-emerald-600" />
        <StatCard label="Pending" value={fmtMoney(fin?.pendingPayment)} accent="text-[#B45309]" />
      </div>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">My Production Jobs</p>
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Job #</Th><Th>Product</Th><Th>Size</Th><Th right>Assigned</Th><Th right>Done</Th><Th>Status</Th><Th className="text-right">Action</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <tr key={j.id} data-testid="mywork-job-row" className="hover:bg-slate-50/60 transition-colors">
                  <Td><span className="font-mono text-xs font-semibold">{j.jobNumber}</span><span className="block text-xs text-slate-400">{fmtDate(j.date)}</span></Td>
                  <Td className="font-medium text-slate-800">{products.find((p) => p.id === j.productId)?.name || '—'}</Td>
                  <Td>{j.size || 'Universal'}</Td>
                  <Td right>{fmtNum(j.quantityAssigned)}</Td>
                  <Td right>{fmtNum(j.quantityCompleted)}</Td>
                  <Td><Badge className={statusColor(j.status)}>{j.status.replace('_', ' ')}</Badge></Td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {j.status !== 'completed' && (
                      <Button size="sm" variant="success" onClick={() => setComplete(j)} data-testid="mywork-complete-btn"><CheckCircle2 size={13} /> Complete</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!jobs.length && <EmptyState title="No jobs assigned to you" icon={Scissors} />}
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">My Output Logs</p>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th>Product</Th><Th>Size</Th><Th right>Dozens</Th><Th right>Earnings</Th><Th>Status</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((l) => (
                <tr key={l.id} data-testid="mywork-log-row" className="hover:bg-slate-50/60 transition-colors">
                  <Td>{fmtDate(l.date)}</Td><Td className="font-medium text-slate-800">{l.productName}</Td><Td>{l.size || 'Universal'}</Td>
                  <Td right>{fmtNum(l.quantityDozens)}</Td><Td right>{fmtMoney(l.totalEarnings)}</Td>
                  <Td>{l.approved ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Approved</Badge> : <Badge className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!logs.length && <EmptyState title="No output logged yet" description="Submit your daily sewn dozens for approval." icon={ClipboardList} />}
      </Card>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 mt-8">My Date-wise Production</p>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Date</Th><Th>Products</Th><Th right>Dozens</Th><Th right>Pieces</Th><Th right>Earnings</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {groupLogsByDate(logs).map((r) => (
                <tr key={r.date} data-testid="mywork-datewise-row" className="hover:bg-slate-50/60 transition-colors">
                  <Td className="font-medium text-slate-800">{fmtDate(r.date)}</Td>
                  <Td className="text-xs text-slate-500">{Object.entries(r.products).map(([n, q]) => `${n} (${q} dz)`).join(', ')}</Td>
                  <Td right className="num font-semibold">{fmtNum(r.dozens)}</Td>
                  <Td right className="num">{fmtNum(r.pieces)}</Td>
                  <Td right className="num">{fmtMoney(r.earnings)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!logs.length && <EmptyState title="No production yet" icon={ClipboardList} />}
      </Card>

      {open && <LogModal tid={tid} products={products} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {complete && <CompleteJobModal job={complete} onClose={() => setComplete(null)} onSaved={() => { setComplete(null); load(); }} />}
    </div>
  );
}

function CompleteJobModal({ job, onClose, onSaved }) {
  const [qty, setQty] = useState(job.quantityAssigned);
  const save = async () => {
    try {
      await api.put(`/jobs/${job.id}/status`, { status: 'completed', quantityCompleted: Number(qty) });
      toast.success('Job completed'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Complete Job · ${job.jobNumber}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button variant="success" onClick={save} data-testid="mywork-save-complete-btn">Mark Complete</Button></>}>
      <Input label="Quantity completed" type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="mywork-complete-qty" />
    </Modal>
  );
}

function LogModal({ tid, products, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [lines, setLines] = useState([{ productId: products[0]?.id || '', dozens: '', size: 'Universal' }]);
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const totalDz = lines.reduce((s, l) => s + (Number(l.dozens) || 0), 0);
  const save = async () => {
    const items = lines.filter((l) => l.productId && Number(l.dozens) > 0)
      .map((l) => ({ productId: l.productId, dozens: Number(l.dozens), size: l.size }));
    if (!items.length) { toast.error('Add at least one product with dozens'); return; }
    try {
      await api.post('/daily-logs', { tailorId: tid, date, items });
      toast.success('Submitted for approval'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title="Log Daily Output" onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-log-btn">Submit</Button></>}>
      <div className="space-y-4">
        <Input label="Date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} data-testid="log-date" />
        <LogLines lines={lines} setLines={setLines} setLine={setLine} products={products} totalDz={totalDz} idPrefix="log" />
      </div>
    </Modal>
  );
}
